import React, { useCallback, useEffect, useState } from 'react';

import { alpha, ButtonBase, Dialog, DialogContent, Typography } from '@mui/material';
import { useAccessGuard } from 'src/components/auth';
import { AlineButton } from 'src/components/generic';
import { FeatureIcon } from 'src/components/icons/FeatureIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  closeCheckoutDialog,
  openCheckoutDialog,
  useCheckoutDialogOpen,
} from './checkout-dialog-var';
import { CheckoutFooter } from './CheckoutFooter';
import type { Feature, PlanType } from './plans';
import { plans } from './plans';

export type CheckoutState = {
  interval: Apollo.SubscriptionInterval;
  numBasicSeatsPurchased: number;
  numFullSeatsPurchased: number;
  seatChanged: boolean;
  selectedTier: Apollo.SubscriptionTier;
};

export function Checkout(): JSX.Element | null {
  const currentUser = useSelectCurrentUser();
  const isSubscribed =
    currentUser?.org.alineSubscription?.status === Apollo.SubscriptionStatus.Active;
  const [createCloseOrderUrl, { loading: closingOrder }] = Apollo.useCreateCloseOrderUrlMutation();
  const openOrder = currentUser?.org.alineSubscription?.openOrders.at(0);

  const handleClick = useAccessGuard({
    callback: async () => {
      if (openOrder) {
        const { data } = await createCloseOrderUrl({ variables: { orderId: openOrder.id } });
        if (data?.createCloseOrderUrl) window.location.href = data.createCloseOrderUrl;
        return;
      }

      openCheckoutDialog();
    },
    minRole: Apollo.OrgRole.Admin,
  });

  return (
    <AlineButton color="primary" loading={closingOrder} variant="contained" onClick={handleClick}>
      {openOrder ? 'Complete Order' : isSubscribed ? 'Upgrade Plan' : 'Subscribe'}
    </AlineButton>
  );
}

const useCheckoutStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(3),
  },
  dialog: {
    borderRadius: 8,
    minWidth: 900,
    padding: theme.spacing(3),
  },
  plans: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  toggle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(1),
  },
}));

export function CheckoutDialog(): JSX.Element {
  const { classes } = useCheckoutStyles();
  const open = useCheckoutDialogOpen();
  const {
    creatingCheckoutUrl,
    currentUser,
    handleCreateCheckoutUrl,
    onSeatDecrement,
    onSeatIncrement,
    setState,
    state,
  } = useCheckout();

  return (
    <Dialog
      classes={{ paper: classes.dialog }}
      open={open}
      slotProps={{
        transition: {
          onExit: () => {
            setState({
              interval: Apollo.SubscriptionInterval.Yearly,
              numBasicSeatsPurchased: 0,
              numFullSeatsPurchased: 0,
              seatChanged: false,
              selectedTier:
                currentUser?.org.alineSubscription?.tier ?? Apollo.SubscriptionTier.Team,
            });
          },
        },
      }}
      onClose={closeCheckoutDialog}
    >
      <DialogContent className={classes.content}>
        <CheckoutHeader />
        <div className={classes.plans}>
          {plans.map(plan => (
            <Plan
              key={plan.id}
              isCurrentPlan={plan.id === state.selectedTier}
              plan={plan}
              setState={setState}
            />
          ))}
        </div>
      </DialogContent>
      <CheckoutFooter
        creatingCheckoutUrl={creatingCheckoutUrl}
        handleCreateCheckoutUrl={handleCreateCheckoutUrl}
        setState={setState}
        state={state}
        onSeatDecrement={onSeatDecrement}
        onSeatIncrement={onSeatIncrement}
      />
    </Dialog>
  );
}

export function CheckoutHeader() {
  return (
    <Typography align="center" variant="h1">
      Pick The Right Plan For You
    </Typography>
  );
}

export function Plan({
  isCurrentPlan,
  plan,
  setState,
}: {
  isCurrentPlan: boolean;
  plan: PlanType;
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
}) {
  const currentUser = useSelectCurrentUser();
  const isSubscribedToPlan = currentUser?.org.alineSubscription?.tier === plan.id;

  function handlePlanClick() {
    setState(prev => ({
      ...prev,
      selectedTier: plan.id,
    }));
  }

  return (
    <PlanWrapper isCurrentPlan={isCurrentPlan} onClick={handlePlanClick}>
      <Typography noWrap variant="h2">
        {isSubscribedToPlan ? `${plan.name} (Current Plan)` : plan.name}
      </Typography>
      <FeatureList features={plan.features} />
    </PlanWrapper>
  );
}

const usePriceSeatStyles = makeStyles()(theme => ({
  featureList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    margin: 0,
    paddingInlineStart: theme.spacing(2),
  },
  priceSeat: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(1),
  },
  priceSeatAction: {
    alignItems: 'center',
    backgroundColor: theme.appColors.darkBackground,
    borderRadius: '50%',
    display: 'flex',
    height: 24,
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    width: 24,
  },
  priceSeatActions: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(0.5),
  },
  priceSeatContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    width: '100%',
  },
}));

export function PriceSeat({
  features,
  label,
  onDecrement,
  onIncrement,
  value,
}: {
  features: string[];
  label: string;
  onDecrement: () => void;
  onIncrement: () => void;
  value: number;
}) {
  const { classes } = usePriceSeatStyles();

  return (
    <div className={classes.priceSeatContainer}>
      <div className={classes.priceSeat}>
        <Typography variant="subtitle2">{label}</Typography>
        <div className={classes.priceSeatActions}>
          <ButtonBase className={classes.priceSeatAction} onClick={onDecrement}>
            -
          </ButtonBase>
          <ButtonBase className={classes.priceSeatAction} onClick={onIncrement}>
            +
          </ButtonBase>
        </div>
        <Typography variant="body1">{value}</Typography>
      </div>
      <ul className={classes.featureList}>
        {features.map((feature, index) => (
          <li key={index}>
            <Typography variant="body1">{feature}</Typography>
          </li>
        ))}
      </ul>
    </div>
  );
}

const usePriceDetailsStyles = makeStyles()(theme => ({
  dollarSign: {
    fontSize: 24,
  },
  priceContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(1),
  },
  priceDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  priceText: {
    fontSize: 40,
  },
}));

export function PriceDetails({
  currentInterval,
  plan,
}: {
  currentInterval: Apollo.SubscriptionInterval;
  plan: PlanType;
}) {
  const { classes } = usePriceDetailsStyles();
  const fullPrice =
    currentInterval === Apollo.SubscriptionInterval.Yearly
      ? plan.price.seats.full.yearly
      : plan.price.seats.full.monthly;
  const basicPrice =
    currentInterval === Apollo.SubscriptionInterval.Yearly
      ? plan.price.seats.basic.yearly
      : plan.price.seats.basic.monthly;
  const intervalText = currentInterval === Apollo.SubscriptionInterval.Yearly ? 'year' : 'month';

  const formatPrice = (price: number): string => {
    const formatter = new Intl.NumberFormat('en-US', {
      compactDisplay: 'short',
      maximumFractionDigits: 1,
      notation: 'compact',
    });

    return formatter.format(price);
  };

  return (
    <div className={classes.priceDetails}>
      <div className={classes.priceContainer}>
        <Typography className={classes.priceText} variant="h1">
          {fullPrice === 'Contact Us'
            ? 'Contact Us'
            : `$${formatPrice(fullPrice)}/user/${intervalText}`}
        </Typography>
      </div>
      <Typography color="textSecondary" variant="body1">
        {basicPrice === 'Contact Us'
          ? 'Contact Us'
          : `$${formatPrice(basicPrice)}/basic user/${intervalText}`}
      </Typography>
    </div>
  );
}

const usePlanWrapperStyles = makeStyles<{ isCurrentPlan: boolean }>()(
  (theme, { isCurrentPlan }) => ({
    planWrapper: {
      '&:hover': {
        border: `1px solid ${theme.appColors.primary}`,
      },
      backgroundColor: isCurrentPlan ? alpha(theme.appColors.primary, 0.1) : undefined,
      border: isCurrentPlan
        ? `1px solid ${theme.appColors.primary}`
        : `1px solid ${theme.appColors.rule}`,
      borderRadius: 8,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
    },
  })
);

export function PlanWrapper({
  children,
  isCurrentPlan,
  onClick,
}: {
  children: React.ReactNode;
  isCurrentPlan: boolean;
  onClick: () => void;
}) {
  const { classes } = usePlanWrapperStyles({ isCurrentPlan });

  return (
    <div className={classes.planWrapper} onClick={onClick}>
      {children}
    </div>
  );
}

const useFeatureListStyles = makeStyles()(theme => ({
  featureItem: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(1),
  },
  featureItemText: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
  },
  featureList: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
}));

export function FeatureList({ features }: { features: Feature[] }) {
  const { classes } = useFeatureListStyles();

  return (
    <ul className={classes.featureList}>
      {features.map(feature => (
        <li key={feature.text} className={classes.featureItem}>
          <div>
            <FeatureIcon />
          </div>
          <div className={classes.featureItemText}>
            <Typography variant="subtitle2">{feature.title}</Typography>
            <Typography variant="body2">{feature.text}</Typography>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function useCheckout() {
  const currentUser = useSelectCurrentUser();
  const [createCheckoutUrl, { loading: creatingCheckoutUrl }] =
    Apollo.useCreateCheckoutUrlMutation();

  /**
   * Initialize numBasic/numFullSeatsPurchased with greater of what they already purchased or their
   * current org seat count so they can't subscribe for less than the users they are using.
   */
  const [state, setState] = useState<CheckoutState>({
    interval: Apollo.SubscriptionInterval.Yearly,
    numBasicSeatsPurchased: 0,
    numFullSeatsPurchased: 0,
    seatChanged: false,
    selectedTier: Apollo.SubscriptionTier.Team,
  });

  const updateSeats = useCallback((seatType: Apollo.OrgRole, increment: boolean) => {
    setState(prevState => {
      const { numBasicSeatsPurchased, numFullSeatsPurchased } = prevState;
      const seatCount =
        seatType === Apollo.OrgRole.Basic ? numBasicSeatsPurchased : numFullSeatsPurchased;
      const updatedCount = Math.max(0, increment ? seatCount + 1 : seatCount - 1);

      switch (seatType) {
        case Apollo.OrgRole.Basic: {
          return {
            ...prevState,
            numBasicSeatsPurchased: updatedCount,
            seatChanged: true,
          };
        }
        case Apollo.OrgRole.Full:
        default: {
          return {
            ...prevState,
            numFullSeatsPurchased: updatedCount,
            seatChanged: true,
          };
        }
      }
    });
  }, []);
  const handleCreateCheckoutUrl = useCallback(async () => {
    const { data } = await createCheckoutUrl({
      variables: {
        input: {
          interval: state.interval,
          numNewBasicSeats: state.numBasicSeatsPurchased,
          numNewFullSeats: state.numFullSeatsPurchased,
          tier: state.selectedTier,
        },
      },
    });
    if (data?.createCheckoutUrl) window.location.href = data.createCheckoutUrl;
  }, [createCheckoutUrl, state]);
  const onSeatDecrement = useCallback(
    (seatType: Apollo.OrgRole) => updateSeats(seatType, false),
    [updateSeats]
  );
  const onSeatIncrement = useCallback(
    (seatType: Apollo.OrgRole) => updateSeats(seatType, true),
    [updateSeats]
  );

  useEffect(() => {
    if (currentUser?.org.alineSubscription) {
      setState(prevState => ({
        ...prevState,
        selectedTier: currentUser?.org.alineSubscription?.tier ?? Apollo.SubscriptionTier.Team,
      }));
    }
  }, [currentUser?.org.alineSubscription]);

  return {
    creatingCheckoutUrl,
    currentUser,
    handleCreateCheckoutUrl,
    onSeatDecrement,
    onSeatIncrement,
    setState,
    state,
  };
}
